import React from 'react';
import { Box, Chip, styled } from '@mui/material';
import { Circle } from '@mui/icons-material';

interface StatusIndicatorProps {
  status: 'online' | 'recording' | 'idle' | 'offline';
  label?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'dot' | 'chip';
}

const StatusDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'status' && prop !== 'size',
})<{ status: string; size: string }>(({ status, size }) => {
  const sizes = {
    small: 8,
    medium: 12,
    large: 16,
  };

  const colors = {
    online: '#00E676',
    recording: '#FF5252',
    idle: '#FFB300',
    offline: '#9E9E9E',
  };

  return {
    width: sizes[size as keyof typeof sizes],
    height: sizes[size as keyof typeof sizes],
    borderRadius: '50%',
    backgroundColor: colors[status as keyof typeof colors],
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: '50%',
      backgroundColor: colors[status as keyof typeof colors],
      animation: status === 'recording' || status === 'online' ? 'pulse 2s infinite' : 'none',
    },
    '@keyframes pulse': {
      '0%': {
        transform: 'scale(1)',
        opacity: 1,
      },
      '100%': {
        transform: 'scale(2)',
        opacity: 0,
      },
    },
  };
});

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'status',
})<{ status: string }>(({ status }) => {
  const colors = {
    online: {
      backgroundColor: 'rgba(0, 230, 118, 0.1)',
      color: '#00E676',
      border: '1px solid rgba(0, 230, 118, 0.3)',
    },
    recording: {
      backgroundColor: 'rgba(255, 82, 82, 0.1)',
      color: '#FF5252',
      border: '1px solid rgba(255, 82, 82, 0.3)',
    },
    idle: {
      backgroundColor: 'rgba(255, 179, 0, 0.1)',
      color: '#FFB300',
      border: '1px solid rgba(255, 179, 0, 0.3)',
    },
    offline: {
      backgroundColor: 'rgba(158, 158, 158, 0.1)',
      color: '#9E9E9E',
      border: '1px solid rgba(158, 158, 158, 0.3)',
    },
  };

  return {
    ...colors[status as keyof typeof colors],
    fontSize: '0.75rem',
    height: 24,
    '& .MuiChip-icon': {
      color: 'inherit',
    },
  };
});

export const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  status,
  label,
  size = 'medium',
  variant = 'dot',
}) => {
  const statusLabels = {
    online: 'Online',
    recording: 'Recording',
    idle: 'Idle',
    offline: 'Offline',
  };

  if (variant === 'chip') {
    return (
      <StatusChip
        status={status}
        icon={<Circle sx={{ fontSize: '0.8rem !important' }} />}
        label={label || statusLabels[status]}
        size="small"
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <StatusDot status={status} size={size} />
      {label && (
        <Box component="span" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
          {label}
        </Box>
      )}
    </Box>
  );
};